import dayjs from "dayjs";
import { TFunction } from "../../../packages/locales/translations/util/useTranslation";

/**
 * 2025-08-26T12:50:05.251Z -> 2025-08-26 14:50
 * @param iso timestamp in UTC
 * @returns formatted string in user timezone
 */
export function formatDateTime(iso: string) {
  const date = new Date(Date.parse(iso));
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")} ${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

/**
 * Result will be correctly negative for future dates.
 * @param date e.g. '2026-02-23'
 */
export function calcDaysOverdue(date: string) {
  return dayjs().startOf("day").diff(dayjs(date), "day");
}

/**
 * Only gives a valid result if daysOverdue > 0.
 */
export function calcOverdueString(daysOverdue: number, t: TFunction) {
  if (daysOverdue === 1) {
    return t("oneDayOverdueString");
  } else {
    return t("daysOverdueString", { daysOverdue });
  }
}

type DateProps = {
  dateTime: string;
  t: TFunction;
};

export function timestampToTimeDiff({ dateTime, t }: DateProps): string {
  if (!dateTime) return "";
  const localDate = new Date(dateTime);
  const currentTime = dayjs();
  const timeDifferenceMinutes = currentTime.diff(localDate, "minutes");
  const timeDifferenceHours = currentTime.diff(localDate, "hours");
  const timeDifferenceDays = currentTime.diff(localDate, "days");

  const getTimeString = (
    difference: number,
    singularKey: string,
    pluralKey: string
  ) =>
    `${difference} ${difference === 1 ? t(singularKey) : t(pluralKey)} ${t(
      "agoString"
    )}`;

  if (timeDifferenceMinutes === 0) return t("justNowString");
  if (timeDifferenceMinutes < 60)
    return getTimeString(
      timeDifferenceMinutes,
      "minuteString",
      "minutesString"
    );
  if (timeDifferenceHours < 24)
    return getTimeString(timeDifferenceHours, "hourString", "hoursString");
  if (timeDifferenceDays <= 5)
    return getTimeString(timeDifferenceDays, "dayString", "daysString");

  return localDate.toISOString().split("T")[0]!;
}
